(() => {
  class ProductDetail extends HTMLElement {
    constructor() {
      super();
      this.prefetchGalleryColor = this.prefetchGalleryColor.bind(this);
    }

    swatchUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;

    connectedCallback() {
      // Smart Wishlist calls this after rendering its button. Sync every pdp on the page (main + quickview)
      // and only fetch status here, reloading would re-trigger this callback
      window.SWCallbackWishlistButtonLoaded = () => {
        document.querySelectorAll('product-detail').forEach(pdp => pdp.syncWishlistStatus?.());
      };

      this.quickviewEl = this.closest('quick-view');
      this.isQuickview = this.quickviewEl !== null;
      this.pdpEls();
      this.pdpSettings();
      this.pdpListeners();
      this.loadRecsEarly();
      this.setRecentlyViewed();

      // start the gallery request on intent so most of it is done by the time a color is picked
      ['focusin', 'pointerover', 'touchstart'].forEach(type => this.addEventListener(type, this.prefetchGalleryColor, { passive: true }));

      this.swatchUnsubscriber = window.subscribe(PUB_SUB_EVENTS.swatchChange, event => {
        if (event.source !== 'product-detail') return;
        const isQuickviewEvent = event.isQuickview || false;
        this.pdpEl = event.pdpEl || this.pdpEl;
        this.blockEls = this.pdpEl.querySelectorAll(`${this.infoElSelector} [data-block-type]`);
        this.infoEl = this.pdpEl.querySelector(this.infoElSelector);
        this.elsToUpdate = Array.from(this.blockEls).map(el => `[data-block-type="${el.dataset.blockType}"]`);
        this.elsToUpdate.push('[data-pdp-gallery]');
        if (event.el) this.clickedSwatchEl = event.el;
        if (this.pdpEl.querySelector('[data-product-media-zoom-modal]')) this.elsToUpdate.push('[data-product-media-zoom-modal]');

        this.currentVariant = event.currentVariant;

        if (!this.currentVariant) {
          this.setUnavailable();
        } else {
          this.setAvailable();
        }

        this.optionValues = event.optionValues;
        this.onChange(event.url, isQuickviewEvent);
      });

      this.variantChangeUnsubscriber = window.subscribe(PUB_SUB_EVENTS.variantChange, event => {
        // quickviews reuse the PDP section id, so match quickviews by element and keep the PDP to its own (non-quickview) selects
        const isOwnEvent = this.isQuickview ? this.contains(event.el) : event.sectionId === this.sectionId && !event.isQuickview;
        if (!isOwnEvent) return;
        this.currentVariant = event.currentVariant;

        // quickviews share section ids, only update the gallery for this pdp's own picker
        if (event.el && this.contains(event.el)) this.updateGalleryColor();
        this.onVariantChange();
      });
    }

    disconnectedCallback() {
      const unsubscribers = [
        this.swatchUnsubscriber,
        this.variantChangeUnsubscriber,
      ];

      unsubscribers.forEach(unsubscriber => unsubscriber && unsubscriber());
      ['focusin', 'pointerover', 'touchstart'].forEach(type => this.removeEventListener(type, this.prefetchGalleryColor));
    }

    getCurrentVariant() {
      // Find the variant matching the form's variant id
      const vId = this.querySelector('.product-form [name="id"]')?.value;
      if (!vId) return null;

      const parseJson = el => {
        if (!el) return null;
        try {
          return JSON.parse(el.textContent);
        } catch (e) {
          return null;
        }
      };
      const findVariant = variants => variants?.find(variant => String(variant.id) === String(vId)) || null;

      // product variants json is re-rendered with the product, so check it first
      const productVariants = parseJson(this.querySelector('[type="application/json"][data-json="product-variants"]'));
      const productVariant = findVariant(productVariants);
      if (productVariant) return productVariant;

      // fall back to combined listing swatches json
      const swatchesJson = parseJson(this.querySelector('[data-swatches-json][type="application/json"]'));
      if (!swatchesJson) return null;

      for (const product of Object.values(swatchesJson)) {
        const match = findVariant(product.variants);
        if (match) return match;
      }

      return null;
    }

    loadRecsEarly() {
      // Instead of waiting for recs to get scrolled into view, start loading when page is idle
      const pdpHasRecs = document.querySelector('product-recommendations');
      const pdpHasRecentlyViewed = document.querySelector('recently-viewed');
      if (!(pdpHasRecs || pdpHasRecentlyViewed) || typeof window.requestIdleCallback !== 'function') return;

      window.requestIdleCallback(deadline => {
        while (deadline.timeRemaining() > 0) {
          lazyLoadScript({
            src: window.assetUrls['product-recommendations.js']
          });
        }
        if (pdpHasRecentlyViewed) {
          lazyLoadScript({
            src: window.assetUrls['recently-viewed.js']
          });
        }
      }, { timeout: 5000 });
    }

    setRecentlyViewed() {
      const handle = this.dataset.productHandle;

      if (localStorage.getItem('product_recently_viewed')) {
        const handles = JSON.parse(localStorage.getItem('product_recently_viewed'));
        const index = handles.indexOf(handle);
        if (index !== -1) {
          handles.splice(index, 1);
          handles.unshift(handle);
        } else {
          handles.push(handle);
        }
        localStorage.setItem('product_recently_viewed', JSON.stringify(handles));
      } else {
        localStorage.setItem('product_recently_viewed', JSON.stringify([handle]));
      }
    }

    pdpEls() {
      this.pdpEl = this;
      this.sectionId = this.closest('product-detail').dataset.sectionId;
      this.infoElSelector = '[data-pdp-info]';

      this.blockEls = this.querySelectorAll(`${this.infoElSelector} [data-block-type]`);
      this.infoEl = this.querySelector(this.infoElSelector);
      this.pageSectionEls = this.closest('main')?.querySelectorAll('.shopify-section');
    }

    pdpSettings() {
      this.elsToUpdate = Array.from(this.blockEls).map(el => `[data-block-type="${el.dataset.blockType}"]`);
      this.elsToUpdate.push('[data-pdp-gallery]');
      if (this.pdpEl.querySelector('[data-product-media-zoom-modal]')) this.elsToUpdate.push('[data-product-media-zoom-modal]');
      this.pageSectionIds = [];
      if (!this.isQuickview) this.pageSectionEls.forEach(section => this.pageSectionIds.push(section.id.split('shopify-section-')[1]));

      const installmentsEl = this.querySelector('#product-form-installment');
      if (installmentsEl) {
        this.productFormInstallment = { el: installmentsEl };
        this.productFormInstallment.id = this.productFormInstallment.el.querySelector('[name="id"]');
        this.productFormInstallment.productId = this.productFormInstallment.el.querySelector('[name="product-id"]');
      }

      /* store page sections in chunks of 5 (sections rendering api limit per call) */
      this.apiSectionLimit = 5;
      this.sectionEls = [];
      this.sectionIds = [];
      document.querySelectorAll('main > .shopify-section:not(.shopify-section--product)').forEach(el => {
        if (!el.classList.contains('shopify-section--yotpo-reviews')) {
          this.sectionEls.push(el);
          this.sectionIds.push(el.id.split('shopify-section-')[1]);
        }
      });
      this.sectionElsChunks = [];
      this.sectionIdsChunks = [];
      while (this.sectionIds.length > 0) {
        this.sectionElsChunks.push(this.sectionEls.splice(0, this.apiSectionLimit));
        this.sectionIdsChunks.push(this.sectionIds.splice(0, this.apiSectionLimit));
      }
    }

    pdpListeners() {
      if (this.isQuickview) return;

      document.addEventListener('click', e => {
        if (!(e.target.closest('[href="#looxReviewsFrame"]'))) return;
        e.preventDefault();
        const target = document.querySelector('#looxReviewsFrame');
        if (!target) return
        window.scrollToEl(target);
      });

      const onHistoryChange = () => {
        const updateActiveSwatch = true;
        // url already has the variant from history, don't let a previous swatch click's option values override it
        this.optionValues = null;
        this.updateContent({ url: location.href, updateActiveSwatch, isQuickview: false, fromHistory: true });
      };

      window.addEventListener('popstate', onHistoryChange);
    }

    onChange(url, isQuickviewEvent = false) {
      const notQuickview = !this.isQuickview && !isQuickviewEvent;

      this.updateContent({ url, updateActiveSwatch: true, isQuickview: !notQuickview });
    }

    onVariantChange() {
      // the sticky form belongs to the PDP, quickviews shouldn't change it
      if (!this.isQuickview) {
        window.publish(PUB_SUB_EVENTS.stickyFormUpdate, {
          currentVariant: this.currentVariant,
        });
      }

      const params = new URLSearchParams(window.location.search);

      if (this.currentVariant) {
        params.set('variant', this.currentVariant.id);
      } else {
        params.delete('variant');
      }

      const url = `${window.location.pathname}?${params.toString()}`;
      this.updateUrl(url, { replace: true });
      this.updateLowStock();
      this.updateWishlistIcon();
      window.updateAfterpayPlacements(this, this.currentVariant);

      // re-query, pickup info may have been re-rendered by a swatch change
      if (this.currentVariant && this.querySelector('[data-store-pickup-info]')) {
        this.updateStorePickup();
      }
    }

    updateLowStock() {
      // re-query, the block and inventory json are re-rendered on swatch change
      const lowStockEl = this.querySelector('[data-low-stock-threshold]');
      if (!lowStockEl) return;

      const messageEl = lowStockEl.querySelector('.product__low-stock') || lowStockEl;
      const lowStockThreshold = parseInt(lowStockEl.dataset.lowStockThreshold, 10);
      const inventoryEl = this.querySelector('[type="application/json"][data-json="product-variants-inventory"]');

      let inventoryData = null;
      try {
        inventoryData = inventoryEl ? JSON.parse(inventoryEl.textContent) : null;
      } catch (e) {
        inventoryData = null;
      }

      const inventory = this.currentVariant && inventoryData ? inventoryData[this.currentVariant.id] : undefined;

      // matches the liquid logic in _product-low-stock
      const qualifies = Boolean(lowStockThreshold)
        && this.currentVariant?.available !== false
        && typeof inventory === 'number'
        && inventory > 0
        && inventory <= lowStockThreshold;

      messageEl.classList.toggle('hide', !qualifies);
    }

    setAvailable() {
      this.isUnavailable = false;
      this.querySelector('[data-pdp-info]')?.classList.remove('variant-unavailable');
    }

    setUnavailable() {
      this.isUnavailable = true;
      this.querySelector('[data-pdp-info]')?.classList.add('variant-unavailable');
    }

    updateActiveSwatch(url) {
      const activeSwatchEl = this.infoEl.querySelector(`.product-swatches__item--active`);
      if (activeSwatchEl) activeSwatchEl.classList.remove('product-swatches__item--active');
      const newActiveSwatchEl = this.infoEl.querySelector(`[data-swatch-item="${url.split('/products/')[1].split('?')[0]}"]`);
      newActiveSwatchEl && newActiveSwatchEl.classList.add('product-swatches__item--active');
    }

    updateStorePickup() {
      fetch(`${window.Shopify?.routes?.root || '/'}products/${this.dataset.productHandle}?variant=${this.currentVariant.id}&section_id=product-pickup-availability`)
        .then(response => response.text())
        .then(responseText => {
          const parsedHtml = new DOMParser().parseFromString(responseText, 'text/html');
          const newPickupInfoEl = parsedHtml.querySelector('[data-store-pickup-info]');
          const currentPickupInfoEl = this.querySelector('[data-store-pickup-info]');
          if (newPickupInfoEl && currentPickupInfoEl) {
            currentPickupInfoEl.innerHTML = newPickupInfoEl.innerHTML;
          }
        })
        .catch(e => console.error(e));
    }

    gallerySectionUrl(variantId) {
      return `${window.Shopify?.routes?.root || '/'}products/${this.dataset.productHandle}?variant=${variantId}&section_id=${this.sectionId}`;
    }

    prefetchGalleryColor(event) {
      const colorIndex = this.dataset.galleryColorIndex;
      if (colorIndex === undefined || !window.fetchSectionHtml) return;

      const label = event.target.closest?.(`[data-product-option="${colorIndex}"] .product-form__label`);
      const input = label?.control;
      if (!input || input.checked || !input.dataset.variantId) return;

      window.fetchSectionHtml(this.gallerySectionUrl(input.dataset.variantId)).catch(() => {});
    }

    setGalleryLoading(isLoading) {
      // the gallery can be scrolled out of view, so also show loading on the clicked color option
      const gallery = this.querySelector('[data-pdp-gallery]');
      const optionGroup = this.querySelector(`[data-product-option="${this.dataset.galleryColorIndex}"]`);

      if (gallery) {
        gallery.classList.toggle('pdp__media--loading', isLoading);
        gallery.setAttribute('aria-busy', isLoading);
      }

      this.querySelectorAll('.product-form__label--loading').forEach(el => el.classList.remove('product-form__label--loading'));
      if (!optionGroup) return;

      optionGroup.setAttribute('aria-busy', isLoading);
      if (isLoading) optionGroup.querySelector('input:checked + .product-form__label')?.classList.add('product-form__label--loading');
    }

    updateGalleryColor() {
      // Variant color products can tag media to a color via alt prefix (see main-product.liquid),
      // re-render the gallery + zoom modal for the new color
      const colorIndex = this.dataset.galleryColorIndex;
      if (colorIndex === undefined || !this.currentVariant) return;

      const color = this.currentVariant.options[colorIndex];
      if (color === this.dataset.galleryColor) return;
      this.dataset.galleryColor = color;
      this.setGalleryLoading(true);

      window.fetchSectionHtml(this.gallerySectionUrl(this.currentVariant.id))
        .then(responseText => {
          // ignore if the color changed again before this finished
          if (color !== this.dataset.galleryColor) return;

          const parsedHtml = new DOMParser().parseFromString(responseText, 'text/html');
          ['[data-pdp-gallery]', '[data-product-media-zoom-modal]'].forEach(selector => {
            const el = this.querySelector(selector);
            const parsedEl = parsedHtml.querySelector(selector);
            if (el && parsedEl) el.innerHTML = parsedEl.innerHTML;
          });

          requestAnimationFrame(() => {
            this.quickviewEl?.update();
            window.swipers();
            window.inView();
          });

          // keep the loader up until the new first image can paint, capped so it never hangs
          const firstImg = this.querySelector('[data-pdp-gallery] img');
          const imgReady = firstImg && !firstImg.complete ? firstImg.decode().catch(() => {}) : Promise.resolve();
          const timeout = new Promise(resolve => setTimeout(resolve, 3000));

          Promise.race([imgReady, timeout]).then(() => {
            if (color === this.dataset.galleryColor) this.setGalleryLoading(false);
          });
        })
        .catch(e => {
          console.error(e);
          if (color === this.dataset.galleryColor) this.setGalleryLoading(false);
        });
    }

    updateContent({ url, updateActiveSwatch = false, isQuickview = false, fromHistory = false }) {
      const urlSplit = url.split('?');
      const params = new URLSearchParams(urlSplit[1]);
      if (this.isUnavailable) {
        this.optionValues = [];
      }

      params.set('section_id', this.sectionId);
      if (this.optionValues) params.set('option_values', this.optionValues.join(','));

      // ignore responses from older requests if the user clicks again before this one finishes
      this.contentRequestId = (this.contentRequestId || 0) + 1;
      const requestId = this.contentRequestId;
      const isStale = () => requestId !== this.contentRequestId;

      fetch(`${urlSplit[0]}?${params.toString()}`)
        .then(response => response.text())
        .then(responseText => {
          if (isStale()) return;
          this.updateDetails(responseText);
          window.renderAfterpayPlacements(this);

          // keep handle in sync with the rendered product (used for store pickup)
          const handle = url.split('/products/')[1]?.split(/[?#]/)[0];
          if (handle) this.dataset.productHandle = handle;

          if (updateActiveSwatch) this.updateActiveSwatch(url);
          this.clickedSwatchEl?.classList.remove('loading');

          requestAnimationFrame(() => {
            if (isStale()) return;
            if (!isQuickview) this.updateMainSections(url, isStale);
            this.updateBuyForm(responseText);

            // Form now holds the re-rendered product's selected variant
            const formVariant = this.getCurrentVariant();
            if (formVariant) {
              this.currentVariant = formVariant;
              this.setAvailable();
            }
            if (!isQuickview && !fromHistory) this.updateUrl(url);

            // Let the variant picker match the selected options against the re-rendered product's variants
            // and publish variantChange (sets currentVariant, url, sticky form, wishlist)
            const pickerSelector = 'variant-radios, variant-selects';
            const variantPicker = this.pdpEl.querySelector(pickerSelector) || this.querySelector(pickerSelector);
            if (!variantPicker) {
              this.updateWishlistIcon();
              return;
            }
            variantPicker.variantData = null;
            variantPicker.dispatchEvent(new Event('change'));
          });
        })
        .catch((e) => {
          console.error(e);
        });
    }

    updateDetails(html) {
      this.elsToUpdate.forEach(selector => {
        const el = this.pdpEl.querySelector(selector) || this.querySelector(selector);
        const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
        const parsedEl = parsedHtml.querySelector(selector);
        if (!(el && parsedEl)) return;
        el.innerHTML = parsedEl.innerHTML;
      })

      this.quickviewEl = this.pdpEl.closest('quick-view');
      requestAnimationFrame(() => {
        this.quickviewEl?.update();
        window.swipers();
        window.inView();
      });
    }

    renderMainSections(sectionsHtml, url, chunkIndex) {
      this.sectionElsChunks[chunkIndex].forEach((section, index) => {
        const id = this.sectionIdsChunks[chunkIndex][index];
        const div = document.createElement('div');
        const sectionHtml = sectionsHtml[id];
        if (!sectionHtml) return;

        /* updat section's html */
        div.innerHTML = sectionsHtml[id];
        const html = div.querySelector(`#shopify-section-${id}`).innerHTML;
        if (id === 'sticky-form') {
          if (section.querySelector('sticky-form') && div.querySelector(`sticky-form`)) {
            section.querySelector('sticky-form').innerHTML = div.querySelector(`sticky-form`).innerHTML;
            setTimeout(() => {
              window.publish(PUB_SUB_EVENTS.stickyFormStart, {});
            }, 500);
          }
        } else {
          section.innerHTML = html;
        }

        /* update page title */
        if (index > 0) return;
        const pageTitle = div.querySelector('[data-meta-title]');
        if (pageTitle) document.title = pageTitle.dataset.metaTitle;
      });

      window.swiperLoaded = false;
      requestAnimationFrame(() => {
        window.swipers();
        window.inView();
      });
    }

    updateMainSections(url, isStale = () => false) {
      if (this.isQuickview) return;

      const urlSplit = url.split('?');
      const params = new URLSearchParams(urlSplit[1]);
      if (this.optionValues) params.set('option_values', this.optionValues.join(','));

      this.sectionIdsChunks.forEach((ids, index) => {
        params.append('sections', ids.join(','));

        fetch(`${urlSplit[0]}?${params.toString().replaceAll('%2C', ',')}`).then((response) => {
          if (isStale()) return null;
          /* make sure the page loads without js in case of any errors (429, etc...) */
          if (!response.ok) {
            window.location = url;
            return null;
          }
          return response.text();
        }).then(response => {
          if (!response || isStale()) return;
          this.renderMainSections(JSON.parse(response), url, index);
        });
      });
    }

    updateUrl(url, { replace = false } = {}) {
      if (this.isQuickview) return;

      const urlSplit = url.split('?');
      const params = new URLSearchParams(urlSplit[1]);
      const vId = this.currentVariant ? this.currentVariant.id : this.querySelector('[name="id"]').value;
      if (vId && !this.isUnavailable) {
        params.set('variant', vId);
      } else {
        params.delete('variant');
      }

      url = `${urlSplit[0]}?${params.toString()}`;
      if (url === `${location.pathname}${location.search}`) return;
      window.history[replace ? 'replaceState' : 'pushState']({}, '', url);
    }

    updateBuyForm(html) {
      if (!html) {
        console.error('Unable to update product form');
        return;
      }

      // Fetched product data
      const parsedHtml = new DOMParser().parseFromString(html, 'text/html');
      const parsedForm = parsedHtml.querySelector('product-form');
      if (!parsedForm) return;
      const parsedFormInputEl = parsedForm.querySelector(`#product-form-${this.dataset.sectionId} input[name="id"]`);
      const parsedAtcBtn = parsedForm.querySelector('.pdp__submit');
      const parsedBisBtn = parsedForm.querySelector('.product__bis');

      // Existing DOM els
      const productForm = this.querySelector('product-form');
      const formInputEl = this.querySelector(`#product-form-${this.dataset.sectionId} input[name="id"]`);
      const atcBtn = this.querySelector('.pdp__submit');
      const bisBtn = this.querySelector('.product__bis');

      // Update hidden form input with variant ID
      const id = parsedFormInputEl?.value || null;
      if (formInputEl) formInputEl.value = id;

      // Update ATC and BIS buttons
      if (atcBtn && parsedAtcBtn) atcBtn.replaceWith(parsedAtcBtn);
      if (bisBtn && parsedBisBtn) bisBtn.replaceWith(parsedBisBtn);

      // Sync BIS state with the new product. Re-query the button, the old one was replaced above
      const isBisActive = parsedForm.classList.contains('bis-active');
      productForm?.classList.toggle('bis-active', isBisActive);
      if (!isBisActive) this.querySelector('.product__bis')?.classList.add('hide');

      // Update product info in BIS modal
      const parsedBisProductInfo = parsedHtml.querySelector('[data-bis-product-info]');
      const bisProductInfoEl = this.querySelector('[data-bis-product-info]');
      if (parsedBisProductInfo && bisProductInfoEl) bisProductInfoEl.innerHTML = parsedBisProductInfo.innerHTML;
    }

    getWishlistVariantId() {
      return this.currentVariant?.id || this.querySelector('.product-form [name="id"]')?.value || null;
    }

    updateWishlistIcon() {
      // Point the wishlist button at the current variant before reloading so adds save the right size/color
      const id = this.getWishlistVariantId();
      const wishlistEl = this.querySelector('[data-smartwishlist]');
      if (wishlistEl && id) wishlistEl.dataset.variant = id;

      requestAnimationFrame(() => this.syncWishlistStatus());
    }

    syncWishlistStatus() {
      const id = this.getWishlistVariantId();
      const bookmarkitEl = this.querySelector('#bookmarkit');
      if (!id || !bookmarkitEl) return;

      // ignore responses from older requests, only the latest variant's status should win
      this.wishlistRequestId = (this.wishlistRequestId || 0) + 1;
      const requestId = this.wishlistRequestId;

      // 1. Check if the user is logged in or a guest
      const customerId = typeof SWGetCustomerWishlistId !== 'undefined' ? SWGetCustomerWishlistId() : 0;
      let requestUrl = '/a/wishlist?type=api&version=1';

      if (customerId && customerId !== 0) {
        // User is logged in
        requestUrl += '&customerid=' + customerId;
      } else if (typeof SWGetExpressWishlistId !== 'undefined') {
        // User is a guest
        const wishlistId = SWGetExpressWishlistId();
        requestUrl += '&wishlistid=' + wishlistId;
      }

      // 2. Append a random string to prevent Shopify from caching the XHR request
      requestUrl += '&_=' + Math.random().toString(36).slice(2, 11);

      // 3. Fetch the items
      fetch(requestUrl)
        .then(response => response.json())
        .then(data => {
          if (requestId !== this.wishlistRequestId) return;

          // 4. Check if current variant is in wishlist and update icon
          const items = Array.isArray(data?.items) ? data.items : [];
          const isBookmarked = items.some(item => String(item.variant_id) === String(id));
          bookmarkitEl.setAttribute('status', isBookmarked ? 'bookmarked' : 'unbookmarked');
          if (isBookmarked) {
            bookmarkitEl.classList.add('bookmarked');
            bookmarkitEl.classList.remove('unbookmarked');
            bookmarkitEl.querySelector('.fa')?.classList.remove('fa-heart-o');
            bookmarkitEl.querySelector('.fa')?.classList.add('fa-heart');
          } else {
            bookmarkitEl.classList.add('unbookmarked');
            bookmarkitEl.classList.remove('bookmarked');
            bookmarkitEl.querySelector('.fa')?.classList.remove('fa-heart');
            bookmarkitEl.querySelector('.fa')?.classList.add('fa-heart-o');
          }
        })
        .catch(error => console.error('Error fetching wishlist:', error));
    }
  }

  if (document.documentElement.dataset.templateName.includes('product')) {
    document.addEventListener('DOMContentLoaded', () => {
      if (!customElements.get('product-detail')) {
        customElements.define('product-detail', ProductDetail);
      }
    });
  } else {
    if (!customElements.get('product-detail')) {
      customElements.define('product-detail', ProductDetail);
    }
  }
})();
